import noop from 'lodash/noop'
import {call, put, select, takeEvery, takeLatest, takeLeading} from 'redux-saga/effects'

import history from '../history'

import * as dataDogUtils from '../components/Replacements/utils'
import * as orderActions from '../actions/order'
import * as ordersSelectors from '../store/orders'
import * as replacementsActions from '../actions/replacements'
import * as replacementsSelectors from '../store/replacements'
import * as siteActions from '../actions/site'
import * as track from '../track/track'
import * as userSelectors from '../store/user'
import * as URLS from '../urls'
import {
  getAllUserOrders,
  getOrderItemsToExchange,
  getProductSizes,
  getShippingAddress,
  onStatus,
  submitExchangeRequest,
} from '../api/api'
import {snakeCaseObjectKeys} from '../api/helpers'

export function* startReplacementsWorker() {
  if (window.TBT_ENV === 'prod') {
    yield call(dataDogUtils.initialiseDataDog)
    yield call(dataDogUtils.startSessionReplay)
  }

  yield call(track.eventMgrLinkClicked, 'Request Replacements')
}

export function* startReplacementsSaga() {
  yield takeEvery(replacementsActions.START_REPLACEMENTS, startReplacementsWorker)
}

export function* getEligibleOrderItemsToExchangeWorker() {
  try {
    yield put(orderActions.getOrderItemsToExchangePending())
    const orderId = yield select(replacementsSelectors.currentOrderId)
    if (!orderId) {
      throw new Error('Unable to fetch order items. Missing order ID')
    }

    const response = yield call(getOrderItemsToExchange, orderId)
    const results = onStatus({
      OK: ({json}) => json.results,
      401: () => {
        throw new Error('Unable to authenticate JWT token')
      },
      404: () => {
        throw new Error('Order does not exist')
      },
      DEFAULT: () => {
        throw new Error('Could not fetch order items for order')
      },
    })(response)

    yield put(orderActions.getOrderItemsToExchangeSuccess(results))
  } catch (e) {
    yield put(orderActions.getOrderItemsToExchangeFailure(e))
  }
}

export function* getEligibleOrderItemsToExchangeSaga() {
  yield takeEvery(replacementsActions.START_REPLACEMENTS, getEligibleOrderItemsToExchangeWorker)
}

export function* navigateToReplacementsFlowOnSuccessWorker() {
  yield call(history.push, URLS.REPLACEMENTS)
}

export function* navigateToReplacementsFlowOnSuccessSaga() {
  yield takeEvery(
    orderActions.GET_ORDER_ITEMS_TO_EXCHANGE_SUCCESS,
    navigateToReplacementsFlowOnSuccessWorker,
  )
}

export function* getProductSizesForExchangeWorker() {
  try {
    yield put(replacementsActions.getProductSizesForExchangePending())

    const {productSetId} = yield select(replacementsSelectors.currentItemToExchangeDetails)
    const response = yield call(getProductSizes, productSetId)

    const result = onStatus({
      OK: ({json}) => ({
        id: productSetId,
        ...json,
      }),
      404: () => {
        throw new Error('Unable to fetch product sizes. Product set does not exist')
      },
      400: () => {
        throw new Error('Unable to fetch product sizes. Product set requested must be a single set')
      },
      DEFAULT: () => {
        throw new Error('Unable to fetch product sizes.')
      },
    })(response)

    yield put(replacementsActions.getProductSizesForExchangeSuccess(result))
  } catch (e) {
    yield put(replacementsActions.getProductSizesForExchangeFailure(e))
  }
}

export function* getProductSizesForExchangeSaga() {
  yield takeEvery(
    replacementsActions.GET_PRODUCT_SIZES_FOR_EXCHANGE,
    getProductSizesForExchangeWorker,
  )
}

export function* submitExchangeRequestWorker() {
  try {
    yield put(replacementsActions.submitExchangeRequestPending())

    const orderId = yield select(replacementsSelectors.currentOrderId)
    if (!orderId) {
      throw new Error('Unable to submit exchange request. Missing order ID')
    }

    const exchangePayload = yield select(replacementsSelectors.createExchangeRequestPayload)
    const formattedExchangePayload = {
      ...snakeCaseObjectKeys(exchangePayload),
      items: exchangePayload.items.map(snakeCaseObjectKeys),
    }

    const response = yield call(submitExchangeRequest, orderId, formattedExchangePayload)
    onStatus({
      // We don't need to do anything on a successful response
      OK: noop,
      404: () => {
        throw new Error('Unable to submit exchange request. Order does not exist')
      },
      400: () => {
        throw new Error(
          'Unable to submit exchange request. There are validation errors in the submitted responses',
        )
      },
      500: () => {
        throw new Error(
          'Unable to submit exchange request. There was an issue creating the Zendesk ticket',
        )
      },
      DEFAULT: () => {
        throw new Error('Unable to submit exchange request')
      },
    })(response)

    yield put(replacementsActions.submitExchangeRequestSuccess())
  } catch (e) {
    yield put(replacementsActions.submitExchangeRequestFailure(e))
  }
}

export function* submitExchangeRequestSaga() {
  yield takeEvery(replacementsActions.SUBMIT_EXCHANGE_REQUEST, submitExchangeRequestWorker)
}

export function* getShippingAddressForExchangeWorker() {
  try {
    yield put(replacementsActions.getShippingAddressForExchangePending())

    const orderId = yield select(replacementsSelectors.currentOrderId)
    const response = yield call(getShippingAddress, orderId)
    onStatus({
      OK: noop,
      DEFAULT: () => {
        throw new Error('Unable to fetch shipping address for order')
      },
    })(response)

    yield put(replacementsActions.getShippingAddressForExchangeSuccess(response.json))
  } catch (e) {
    yield put(replacementsActions.getShippingAddressForExchangeFailure(e))
  }
}

export function* getShippingAddressForExchangeSaga() {
  yield takeLeading(replacementsActions.SUBMIT_SUMMARY, getShippingAddressForExchangeWorker)
}

export function* trackExchangeRequestCompleteWorker() {
  const orderId = yield select(replacementsSelectors.currentOrderId)
  yield call(track.completedExchangeRequest, orderId)
}

export function* trackExchangeRequestCompleteSaga() {
  yield takeLeading(
    replacementsActions.SUBMIT_EXCHANGE_REQUEST_SUCCESS,
    trackExchangeRequestCompleteWorker,
  )
}

export function* trackItemExchangeRequestSubmittedWorker() {
  const orderId = yield select(replacementsSelectors.currentOrderId)
  const allSelectedItemsToExchangeDetails = yield select(
    replacementsSelectors.allSelectedItemsToExchangeDetails,
  )
  yield call(track.submittedItemsExchangeRequest, allSelectedItemsToExchangeDetails, orderId)
}

export function* trackItemExchangeRequestSubmittedSaga() {
  yield takeEvery(replacementsActions.SUBMIT_SUMMARY, trackItemExchangeRequestSubmittedWorker)
}

/**
 * Stop data dog's session replay when the user exits the replacement flow.
 */
export function* stopDatadogSessionOnExitFlowWorker() {
  const isSessionRecording = yield call(dataDogUtils.isSessionRecording)

  if (
    window.TBT_ENV === 'prod' &&
    window.location.pathname !== URLS.REPLACEMENTS &&
    isSessionRecording
  ) {
    yield call(dataDogUtils.stopSessionReplay)
  }
}

export function* stopDatadogSessionOnExitFlowSaga() {
  yield takeLatest(siteActions.SUBSEQUENT_PAGE_LOAD, stopDatadogSessionOnExitFlowWorker)
}

/**
 * In order to correctly route /start/replacements, we get all orders for the user, then see if there
 * are upcoming events with a shipped, uncancelled, rental order. If so, we start
 * the replacements flow with the earliest found order. If not, we route the user back to their events page.
 */
export function* getAllUserOrdersWorker() {
  try {
    yield put(orderActions.getAllUserOrdersPending())
    const userId = yield select(userSelectors.id)
    const ordersResponse = yield call(getAllUserOrders, userId)

    const orders = onStatus({
      OK: ({json}) => json.results,
      DEFAULT: ({statusText}) => {
        throw new Error(`Could not get orders for user: ${statusText}`)
      },
    })(ordersResponse)

    yield put(orderActions.getAllUserOrdersSuccess(orders))

    const firstUpcomingRentalOrder = yield select(ordersSelectors.getFirstUpcomingRentalOrder)

    if (firstUpcomingRentalOrder) {
      const {eventId, id: orderId} = firstUpcomingRentalOrder

      yield put(
        replacementsActions.startReplacements({
          eventId,
          orderId,
        }),
      )
    }
  } catch (e) {
    yield put(orderActions.getAllUserOrdersFailure(e))
  }
}

export function* getAllUserOrdersSaga() {
  yield takeLeading(orderActions.GET_ALL_USER_ORDERS, getAllUserOrdersWorker)
}
